import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { getAllStudents, rejectStudent, approveStudent } from '../../../../services/mentor/approvalService';
import type { User } from '../../../../types/user';
import Loader from '../../../../components/common/Loader';
import { showToast } from '../../../../utils/showToast'; // showToast now expects options object
import { Check, X } from 'lucide-react'; // Icons for actions

const Approvals: React.FC = () => {
  const { user, isLoading: isAuthLoading } = useAuth();
  const [pendingStudents, setPendingStudents] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const fetchPendingStudents = async () => {
    setIsLoading(true);
    try {
      const students = await getAllStudents();
      setPendingStudents(students.filter(student => !student.isApproved)); // Filter for only pending students
    } catch (err: any) {
      showToast(err.message, { type: 'error' }); // ✅ Updated showToast call
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthLoading && user && (user.role === 'mentor' || user.isAdmin)) {
      fetchPendingStudents();
    }
  }, [isAuthLoading, user]);

  const handleApproveRejectStudent = async (studentId: string, status: 'approve' | 'reject') => {
    setIsProcessing(true);
    try {
      let message;
      if (status === 'approve') {
        message = await approveStudent(studentId);
      } else {
        message = await rejectStudent(studentId);
      }
      showToast(message, { type: 'success' }); // ✅ Updated showToast call
      // Remove the student from the local state
      setPendingStudents(prev => prev.filter(student => student._id !== studentId));
    } catch (err: any) {
      showToast(err.message || `Failed to ${status} student.`, { type: 'error' }); // ✅ Updated showToast call
    } finally {
      setIsProcessing(false);
    }
  };

  if (isAuthLoading || isLoading) {
    return (
      <div className="flex min-h-64 items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="py-10 px-4">
      <div className="max-w-7xl mx-auto space-y-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Student Approvals</h1>
        {pendingStudents.length === 0 ? (
          <p className="text-center text-gray-500">No students are awaiting approval.</p>
        ) : (
          <div className="overflow-x-auto bg-white rounded-lg shadow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Joined On
                  </th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pendingStudents.map(student => (
                  <tr key={student._id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {student.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {student.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(student.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleApproveRejectStudent(student._id, 'approve')}
                        disabled={isProcessing}
                        className="text-green-600 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Check className="h-5 w-5" /> Approve
                      </button>
                      <button
                        onClick={() => handleApproveRejectStudent(student._id, 'reject')}
                        disabled={isProcessing}
                        className="ml-4 text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <X className="h-5 w-5" /> Reject
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Approvals;
